import {
  ArrowLeftOutlined,
  EditOutlined,
  FileTextOutlined,
  PictureOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Image,
  Modal,
  Progress,
  Row,
  Table,
  theme,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { EditWorkShopRoute } from "..";
import {
  BackbuttonComponent,
  ColumnComponent,
  ContainerComponent,
  FileUploaderComponent,
  RowComponent,
  StatusTagComponent,
} from "../../../components";
import RenderMarkdown from "../../../components/render-markdown.component";
import { useMutation, useQuery } from "../../../hooks";
import { ColumnsWithScrolleableLayout } from "../layouts/columns-with-scrolleable.layout";
import { AgentDetailModal } from "./partials/agent-detail-modal.partial";
import { ProjectSheetModal } from "./partials/project-sheet-modal.partial";

const Container = styled.div`
  padding: 0px 32px 0 32px;
  min-height: 100vh;
`;

const HeaderTitle = styled(Typography.Title)`
  && {
    margin: 0;
    font-size: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBar = styled(Progress)`
  bottom: 0;
  left: 0;
  width: 100%;

  .ant-progress-bg {
    background: var(--color-secondary) !important;
    opacity: 1 !important;
    box-shadow: none !important;
    filter: none !important;
  }
`;

const EditButton = styled(Button)`
  border: none;

  &:hover {
    background-color: #e65100;
  }
`;

const ProjectDetails = styled.div`
  box-shadow: 0 4px 8px rgba(0, 140, 255, 0.05);
  margin-bottom: 16px;
  margin-top: 32px;
  background-color: white;
  border-radius: 32px;
  color: gray;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 32px 32px 0px;
  border-radius: 32px 32px 0 0;
  font-weight: bold;
`;

const SectionContent = styled.div`
  padding: 0px 5px 32px;
  border-radius: 0 0 8px 8px;
  border-top: none;
  font-size: 14px;
  text-align: left;
`;

const PlaceholderIcon = styled(PictureOutlined)`
  font-size: 48px;
  color: #ccc;
`;

const KPIWrapper = styled.div`
  padding: 16px;
  font-size: 14px;
  text-align: left;
`;

const BackButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: 16px;

  &:hover {
    text-decoration: underline;
  }
`;

const ImageSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px dashed #ddd;
  margin: 12px;
  width: "fit-content";
`;

const StyledTable = styled(Table)`
  width: 100%;
  table-layout: auto;

  .ant-table {
    width: 100%;
    border-radius: 8px;
  }
  .ant-table-thead > tr > th {
    color: var(--color-primary);
    text-align: center;
  }
  .ant-table-tbody > tr > td {
    text-align: left;
    vertical-align: middle;
  }
`;
const FilesSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  overflow: auto;
`;

const FileName = styled.div`
  margin-top: 8px;
  font-size: 12px;
  text-align: center;
  color: #333;
`;

const PdfIcon = styled(FileTextOutlined)`
  font-size: 32px;
`;



export function WorkshopDetails() {
  const {
    token: { colorTextSecondary },
  } = theme.useToken();

  const navigate = useNavigate();
  const { id: workshopId } = useParams();

  const [visible, setVisible] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");

  const metricsColumns = [
    { title: "Métrica", dataIndex: "metric", key: "metric" },
    { title: "Descripción", dataIndex: "description", key: "description" },
    { title: "Volumen", dataIndex: "volume", key: "volume" },
  ];

  const metricsAfterColumns = [
    { title: "Métrica", dataIndex: "metric", key: "metric" },
    { title: "Descripción", dataIndex: "description", key: "description" },
    { title: "Volumen", dataIndex: "volume", key: "volume" },
    {
      title: "Variación",
      dataIndex: "variation",
      key: "variation",
      render: (text) => {
        if (!text) return "—";
        const negative = String(text).trim().startsWith("-");
        return (
          <span
            style={{
              color: negative ? "#d6435a" : "#2bb673",
              fontWeight: 700,
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  const columns = [
    {
      title: "Agente",
      dataIndex: "agentName",
      key: "agent",
    },
    {
      title: "Objetivo",
      dataIndex: "objective",
      key: "objective",
      render: (text, record) =>
        text && text.length > 100
          ? (
            <>
              {text.substring(0, 100)}
              <span
                style={{ color: "#1890ff", cursor: "pointer", marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowModal(record);
                }}
              >
                ... Ver más
              </span>
            </>
          )
          : text
    },
    {
      title: "Actividades",
      dataIndex: "activity",
      key: "activities",
      render: (text, record) =>
        text && text.length > 100
          ? (
            <>
              {text.substring(0, 100)}
              <span
                style={{ color: "#1890ff", cursor: "pointer", marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowModal(record);
                }}
              >
                ... Ver más
              </span>
            </>
          )
          : text
    },
    {
      title: "Input",
      dataIndex: "inputs",
      key: "input",
      render: (text, record) =>
        text && text.length > 100
          ? (
            <>
              {text.substring(0, 100)}
              <span
                style={{ color: "#1890ff", cursor: "pointer", marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowModal(record);
                }}
              >
                ... Ver más
              </span>
            </>
          )
          : text
    },
    {
      title: "Output",
      dataIndex: "outputs",
      key: "output",
      render: (text, record) =>
        text && text.length > 100
          ? (
            <>
              {text.substring(0, 100)}
              <span
                style={{ color: "#1890ff", cursor: "pointer", marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowModal(record);
                }}
              >
                ... Ver más
              </span>
            </>
          )
          : text
    },
  ];

  const handleShowModal = (record) => {
    setSelectedRow(record);
    setModalVisible(true);
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [sheetVisible, setSheetVisible] = useState(false);

  const {
    data: workshop,
    loading,
    execute,
  } = useQuery({
    url: `/workshop/${workshopId}`,
    options: { method: "GET" },
    lazy: true,
  });

  const { executeMutation: updateWorkshop } = useMutation({
    url: `/workshop`,
    method: "PUT",
    onSuccess: () => {
      window.location.reload();
    },
    onError: () => {
      alert("Error al actualizar el taller");
    },
  });

  const handleFileClick = (fileUrl) => {
    if (fileUrl.endsWith(".pdf")) {
      window.open(fileUrl, "_blank");
    } else if (fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)) {
      setSelectedImg(fileUrl);
      setVisible(true);
    }
  };

  useEffect(() => {
    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId]);

  if (loading) {
    return (
      <Container>
        <Typography.Title level={2}>Cargando taller...</Typography.Title>
      </Container>
    );
  }

  if (!workshop || !workshop.id) {
    return (
      <Container>
        <Typography.Title level={2}>Proyecto no encontrado</Typography.Title>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Volver a Taller
        </BackButton>
      </Container>
    );
  }

  const workshopDemoUrl =
    workshop?.demoUrl?.trim() || workshop?.demoURL?.trim() || workshop?.videoUrl?.trim() || "";
  const isYoutubeUrl =
    workshopDemoUrl.includes("youtube.com") || workshopDemoUrl.includes("youtu.be");
  const youtubeEmbedUrl = workshopDemoUrl.includes("watch?v=")
    ? workshopDemoUrl.replace("watch?v=", "embed/")
    : workshopDemoUrl.includes("youtu.be/")
      ? workshopDemoUrl.replace("youtu.be/", "youtube.com/embed/")
      : workshopDemoUrl;

  return (
    <>
      <BackbuttonComponent
        style={{
          marginBottom: 16,
          display: "block",
          textAlign: "left",
        }}
      />
      <ContainerComponent style={{ padding: "20px 40px 0px 40px" }}>
        <RowComponent justifyContent="space-between">
          <RowComponent>
            <HeaderTitle level={2} style={{ marginRight: 10 }}>
              {workshop.title || "Proyecto sin titulo"}
            </HeaderTitle>

            <SubHeader>
              <StatusTagComponent status={workshop.status} />
            </SubHeader>
          </RowComponent>

          {/* {user.role === Roles.ADMIN && ( */}
          <RowComponent style={{ justifyContent: "end", gap: 8 }}>
            <EditButton
              icon={<PictureOutlined />}
              onClick={() => setSheetVisible(true)}
              style={{ backgroundColor: "#143462", color: "#fff" }}
            >
              Ver Ficha del Proyecto
            </EditButton>
            <Link to={EditWorkShopRoute({ workshopId: workshop.id })}>
              <EditButton icon={<EditOutlined />}>Editar proyecto</EditButton>
            </Link>
            {/* <EditButton
                style={{ marginLeft: "12px", backgroundColor: "red" }}
                icon={<DeleteOutlined />}
                onClick={() =>
                  deleteWorkshop({
                    id: workshop.id,
                  })
                }
              >
                Eliminar
              </EditButton> */}
          </RowComponent>
          {/* )} */}
        </RowComponent>
        <ColumnComponent>
          <Typography.Text
            style={{
              alignSelf: "start",
              color: colorTextSecondary,
              fontWeight: 500,
            }}
          >
            {workshop.status === "pending"
              ? "1/3 Pendiente"
              : workshop.status === "on_progress"
                ? "2/3 En Proceso"
                : workshop.status === "completed"
                  ? "3/3 Completado"
                  : "Sin estatus"}
          </Typography.Text>
          <RowComponent style={{ gap: 32 }}>
            <ProgressBar
              percent={
                ["pending", "on_progress", "completed"].includes(
                  workshop.status
                )
                  ? 100
                  : 0
              }
              showInfo={false}
              strokeColor="var(--color-secondary)"
            />
            <ProgressBar
              percent={
                ["on_progress", "completed"].includes(workshop.status) ? 100 : 0
              }
              showInfo={false}
              strokeColor="var(--color-secondary)"
            />
            <ProgressBar
              percent={["completed"].includes(workshop.status) ? 100 : 0}
              showInfo={false}
              strokeColor="var(--color-secondary)"
            />
          </RowComponent>
        </ColumnComponent>
      </ContainerComponent>
      <ColumnsWithScrolleableLayout
        rightContent={
          <ColumnComponent>
            <ProjectDetails>
              <SectionTitle>
                <ColumnComponent style={{ alignItems: "flex-start" }}>
                  <Typography.Title level={4} style={{ marginBottom: 0 }}>
                    Documentos
                  </Typography.Title>

                  <ColumnComponent style={{ alignItems: "flex-center" }}>
                    <FileUploaderComponent
                      // validUrls is a list of strings
                      onFileUploaded={(validUrls) => {
                        if (validUrls.length > 0) {
                          updateWorkshop({
                            ...workshop,
                            filesUrl: [
                              ...(workshop.filesUrl || []),
                              ...validUrls,
                            ],
                          });
                        }
                      }}
                    />
                    <Typography.Text
                      style={{ color: colorTextSecondary, marginBottom: 32 }}
                    >
                      solo archivos . docx, xlsx y pdf permitidos
                    </Typography.Text>
                  </ColumnComponent>
                  <Typography.Text
                    style={{ color: colorTextSecondary, paddingTop: 0 }}
                  >
                    Documentos cargados (
                    {workshop.filesUrl && workshop.filesUrl.length})
                  </Typography.Text>
                </ColumnComponent>
              </SectionTitle>
              <SectionContent>
                <FilesSection style={{ overflowX: "auto" }}>
                  {workshop.filesUrl && workshop.filesUrl.length > 0 ? (
                    workshop.filesUrl.map((fileUrl, index) => {
                      if (!fileUrl) {
                        return null;
                      }
                      return (
                        <RowComponent
                          key={index}
                          onClick={() => handleFileClick(fileUrl)}
                          style={{
                            gap: 16,
                            cursor: "pointer",
                            marginBottom: 16,
                            flexShrink: 0,
                          }}
                        >
                          <ContainerComponent
                            style={{
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {fileUrl.endsWith(".pdf") ? (
                              <>
                                <PdfIcon />
                                <FileName>Documento PDF</FileName>
                              </>
                            ) : fileUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                              <>
                                <img
                                  src={fileUrl}
                                  alt={`Archivo ${index}`}
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    borderRadius: "4px",
                                    objectFit: "cover",
                                  }}
                                />
                                <FileName>Imagen</FileName>
                              </>
                            ) : (
                              <>
                                <PdfIcon />
                                <FileName>Archivo</FileName>
                              </>
                            )}
                          </ContainerComponent>
                          <ColumnComponent>
                            <Typography.Text
                              style={{ color: colorTextSecondary }}
                            >
                              {fileUrl.split("/").pop()}
                            </Typography.Text>
                          </ColumnComponent>
                        </RowComponent>
                      );
                    })
                  ) : (
                    <Typography.Text style={{ color: colorTextSecondary }}>
                      No hay archivos cargados
                    </Typography.Text>
                  )}
                </FilesSection>
              </SectionContent>
            </ProjectDetails>

            {/* <ProjectDetails>
              <SectionTitle>
                <ColumnComponent style={{ alignItems: "flex-start" }}>
                  <Typography.Title level={4} style={{ marginBottom: 0 }}>
                    Configuración
                  </Typography.Title>
                </ColumnComponent>
              </SectionTitle>
              <SectionContent>
                <Typography.Text style={{ color: colorTextSecondary }}>
                  Estado
                </Typography.Text>
                <Select
                  defaultValue={workshop.status}
                  style={{ width: "100%" }}
                  placeholder="Seleccione un estado"
                  onChange={(value) => {
                    updateWorkshop({ ...workshop, status: value });
                  }}
                  options={[
                    { value: "pending", label: "Pendiente" },
                    { value: "on_progress", label: "En progreso" },
                    { value: "completed", label: "Completado" },
                  ]}
                />
              </SectionContent>
            </ProjectDetails> */}
          </ColumnComponent>
        }
        leftContent={
          <Container>
            {/* DESCRIPCIÓN DEL PROBLEMA */}
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Descripción del Problema
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <div style={{ padding: "0px 27px 0px" }}>
                  {workshop.description ? (
                    <RenderMarkdown markdownContent={workshop.description} />
                  ) : (
                    "Sin Texto"
                  )}
                </div>
              </SectionContent>
            </ProjectDetails>
            {/* CONOCIMIENTO TÉCNICO ACTUAL */}
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Conocimiento Técnico Actual
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <Row>
                  <Col span={24} style={{ padding: "0px 27px 0px" }}>
                    {workshop.actualKnowledge ? (
                      <RenderMarkdown
                        markdownContent={workshop.actualKnowledge}
                      />
                    ) : (
                      "Sin Conocimiento técnico actual"
                    )}
                  </Col>
                </Row>
                <Row
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Col
                    span={12}
                    style={{
                      padding: "0px 27px 0px",
                    }}
                  >
                    {workshop.knowledgeImg ? (
                      <Image
                        src={workshop.knowledgeImg}
                        alt="Imagen del proyecto"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          borderRadius: "8px",
                        }}
                      />
                    ) : (
                      <PlaceholderIcon />
                    )}
                  </Col>
                </Row>
              </SectionContent>
            </ProjectDetails>
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>Métricas Antes</Typography.Title>
              </SectionTitle>
              <SectionContent>
                <StyledTable
                  style={{ width: "100%" }}
                  columns={metricsColumns}
                  dataSource={[
                    ...(workshop.metricsBefore?.length
                      ? workshop.metricsBefore
                      : workshop.metrics || []),
                  ]}
                  pagination={false}
                />
              </SectionContent>
            </ProjectDetails>
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>Métricas Después</Typography.Title>
              </SectionTitle>
              <SectionContent>
                {workshop.metricsAfter && workshop.metricsAfter.length > 0 ? (
                  <StyledTable
                    style={{ width: "100%" }}
                    columns={metricsAfterColumns}
                    dataSource={[...workshop.metricsAfter]}
                    pagination={false}
                  />
                ) : (
                  <div style={{ padding: "0 27px", color: colorTextSecondary }}>
                    Sin métricas registradas todavía.
                  </div>
                )}
              </SectionContent>
            </ProjectDetails>
            {/* SECCION PARA CARGAR ARCHIVOS */}
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Objetivo de implementar Agentes
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <div style={{ padding: "0px 27px 0px" }}>
                  {workshop.agentObjective ? (
                    <RenderMarkdown markdownContent={workshop.agentObjective} />
                  ) : (
                    "Información pendiente por capturar"
                  )}
                </div>
              </SectionContent>
            </ProjectDetails>
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>Detallado de la problemática</Typography.Title>
              </SectionTitle>
              <KPIWrapper>
                <SectionContent>
                  <div
                  /* style={{padding: "0px 27px 0px"}} */
                  >
                    {workshop.prompt ? (
                      <RenderMarkdown markdownContent={workshop.prompt} />
                    ) : (
                      "Información pendiente por capturar"
                    )}
                  </div>
                </SectionContent>
              </KPIWrapper>
            </ProjectDetails>
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Pasos del problema
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <div style={{ padding: "0px 27px 0px" }}>
                  {workshop.problemSteps ? (
                    <RenderMarkdown markdownContent={workshop.problemSteps} />
                  ) : (
                    "Información pendiente por capturar"
                  )}
                </div>
              </SectionContent>
            </ProjectDetails>
            {workshopDemoUrl && (
              <ProjectDetails>
                <SectionTitle>
                  <Typography.Title level={3}>
                    Video del taller
                  </Typography.Title>
                </SectionTitle>
                <SectionContent>
                  <div style={{ padding: "0 27px" }}>
                    {isYoutubeUrl ? (
                      <iframe
                        src={youtubeEmbedUrl}
                        title="Video del taller"
                        width="100%"
                        height="420"
                        style={{ border: 0, borderRadius: 12 }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={workshopDemoUrl}
                        controls
                        preload="metadata"
                        style={{ width: "100%", maxHeight: 420, borderRadius: 12 }}
                      >
                        <source src={workshopDemoUrl} />
                      </video>
                    )}
                    <div style={{ marginTop: 12 }}>
                      <a href={workshopDemoUrl} target="_blank" rel="noopener noreferrer">
                        Abrir video en nueva pestaña
                      </a>
                    </div>
                  </div>
                </SectionContent>
              </ProjectDetails>
            )}
            {/* SECCIÓN NUEVA: DIAGRAMA FUNCIONAL */}
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Diagrama Funcional
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <ImageSection>
                  <Image
                    src={
                      workshop.functionalDiagramUrl ||
                      "https://devagentesinteligentes.blob.core.windows.net/agentes-catalogo/under_contruction.png"
                    }
                    alt="Diagrama Funcional"
                    height={200}
                  />
                </ImageSection>
              </SectionContent>
            </ProjectDetails>
            {/* TABLA DE DETALLE DE AGENTES */}
            <ContainerComponent
              style={{
                backgroundColor: "#ffffff",
                padding: 24,
                borderRadius: 16,
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                marginBottom: 24,
              }}
            >
              <Row>
                <Col span={24}>
                  <Typography.Title style={{ textAlign: "left" }} level={3}>
                    Detalle de los Agentes
                  </Typography.Title>
                </Col>
              </Row>
              <Row>
                <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table
                    columns={columns}
                    dataSource={[...workshop.agents]}
                    pagination={false}
                    style={{ minWidth: 600 }}
                  />
                </div>
              </Row>
            </ContainerComponent>
            <AgentDetailModal
              visible={modalVisible}
              onCancel={() => setModalVisible(false)}
              selectedRow={selectedRow}
            />
            {/* <Modal
              visible={modalVisible}
              footer={null}
              onCancel={() => setModalVisible(false)}
              title={
                <Typography.Title style={{ textAlign: "left", marginLeft: 12 }} level={4}>
                  <FileSearchOutlined style={{ marginRight: 4 }} />
                  Detalle de los Agentes
                </Typography.Title>
              }
              centered
              width='70%'
              styles={{
                header: {
                  background: "#fff",
                },
              }}
            >
              {selectedRow && (
                <div style={{ padding: 10 }}>
                  <p><b>Agente:</b> {selectedRow.agentName}</p>
                  <p><b>Objetivo:</b> {selectedRow.objective}</p>
                  <p><b>Actividades:</b> {selectedRow.activity}</p>
                  <p><b>Input:</b> {selectedRow.inputs}</p>
                  <p><b>Output:</b> {selectedRow.outputs}</p>
                </div>
              )}
            </Modal> */}
            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Diagrama Lógico Funcional
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <ImageSection>
                  <Image
                    src={
                      workshop.functionalLogicDiagramUrl ||
                      "https://devagentesinteligentes.blob.core.windows.net/agentes-catalogo/under_contruction.png"
                    }
                    alt="Diagrama Funcional"
                    height={200}
                  />
                </ImageSection>
              </SectionContent>
            </ProjectDetails>

            <ProjectDetails>
              <SectionTitle>
                <Typography.Title level={3}>
                  Diagrama Secuencial de Agentes
                </Typography.Title>
              </SectionTitle>
              <SectionContent>
                <ImageSection>
                  <Image
                    src={
                      workshop.agentsSequenceDiagramUrl ||
                      "https://devagentesinteligentes.blob.core.windows.net/agentes-catalogo/under_contruction.png"
                    }
                    alt="Diagrama Funcional"
                    height={200}
                  />
                </ImageSection>
              </SectionContent>
            </ProjectDetails>

            <Modal
              visible={visible}
              footer={null}
              onCancel={() => setVisible(false)}
              centered
              width={800}
            >
              <img
                src={selectedImg}
                alt="Imagen seleccionada"
                style={{ width: "100%", borderRadius: "8px" }}
              />
            </Modal>

            <BackButton onClick={() => navigate(-1)}>
              <ArrowLeftOutlined /> Volver a proyectos
            </BackButton>
          </Container >
        }
      />
      <ProjectSheetModal
        open={sheetVisible}
        onClose={() => setSheetVisible(false)}
        workshopId={workshopId}
      />
    </>
  );
}
